import { CheckIcon } from "@heroicons/react/24/outline";
import { Link } from "@remix-run/react";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const meta = () => {
  return [
    { title: "Takealot Repricing Plans | Shopdeploy" },
    {
      name: "description",
      content:
        "Discover Shopdeploy's Repricing pricing plans. Find the right fit for your business with our Takealot Seller Software. Get started today.",
    },
  ];
};

export default function Pricing() {
  const pricing = {
    tiers: [
      {
        title: "Starter",
        price: 499,
        frequency: "/month",
        description: "Automated Repricing for 100 products",
        features: [
          "1 Seller Account",
          "3 Users",
          "Live Sales Dashboard",
          "Sales Analytics Tools",
          "Inventory Metrics & Alerts",
          "Unlimited Buy Box Monitoring",
          "New Review Alerts",
          "Mobile Acccess",
          "World Class Support",
        ],
        cta: "14 day Free Trial",
        mostPopular: false,
      },
      {
        title: "Growth",
        price: 799,
        frequency: "/month",
        description: "Automated Repricing for 300 products",
        features: [
          "2 Seller Accounts",
          "5 Users",
          "All Starter Features",
          "Custom Onboarding Consultation",
        ],
        cta: "14 day Free Trial",
        mostPopular: true,
      },
      {
        title: "Retail",
        price: 1499,
        frequency: "/month",
        description: "Automated Repricing for 500 products",
        features: [
          "3 Seller Accounts",
          "8 Users",
          "All Growth Features",
          "Dedicated Account Manager",
          "Custom Reporting",
        ],
        cta: "14 day Free Trial",
        mostPopular: false,
      },
    ],
  };

  const faqs = [
    {
      id: 1,
      question: "Hows does Shopdeploy access my Takealot Marketplace data?",
      answer:
        "We securely access your data through your unique API Key. This key is available in the Takealot Seller Portal under the API Integrations tab",
    },
    {
      id: 2,
      question: "Can I cancel or downgrade at anytime?",
      answer:
        "Yes, you can cancel or change your plan at any time. All plans are month to month and there are no contractual obligations.",
    },
    {
      id: 3,
      question:
        "Can I manage multiple Takealot Seller Portal accounts with Shopdeploy?",
      answer:
        "Yes, you can. If you're interested in adding additional Takealot Seller accounts to your Shopdeploy profile please contact us using the contact form",
    },
    {
      id: 4,
      question: "What do I need to get started??",
      answer:
        "To use Shopdeploy you will need to be a registered Takealot Marketplace Seller with an active Takealot Seller Portal Account",
    },
    {
      id: 5,
      question: "What forms of payment do you accept?",
      answer: "We accept both Credit Cards and EFTs.",
    },
    // More questions...
  ];

  return (
    <>
      <div className="mx-auto max-w-7xl bg-white py-10 md:py-14 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl sm:leading-none lg:text-6xl">
          Pricing plans for sellers of all sizes
        </h1>
        <p className="mt-6 max-w-2xl text-xl text-gray-500">
          Simple, transparent pricing. Always know what you'll Pay.
        </p>

        {/* Tiers */}
        <div className="mt-24 space-y-12 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:space-y-0">
          {pricing.tiers.map((tier) => (
            <div
              key={tier.title}
              className="relative flex flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
            >
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">
                  {tier.title}
                </h3>
                {tier.mostPopular ? (
                  <p className="absolute top-0 -translate-y-1/2 transform rounded-full bg-indigo-500 py-1.5 px-4 text-sm font-semibold text-white">
                    Most popular
                  </p>
                ) : null}
                <p className="mt-4 flex items-baseline text-gray-900">
                  <span className="text-5xl font-bold tracking-tight">
                    R{tier.price}
                  </span>
                  <span className="ml-1 text-xl font-semibold">
                    {tier.frequency}
                  </span>
                </p>
                <p className="mt-6 text-gray-500">{tier.description}</p>

                {/* Feature list */}
                <ul className="mt-6 space-y-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex">
                      <CheckIcon
                        className="h-6 w-6 flex-shrink-0 text-indigo-500"
                        aria-hidden="true"
                      />
                      <span className="ml-3 text-gray-500">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="https://app.shopdeploy.com/signup"
                className={classNames(
                  tier.mostPopular
                    ? "bg-indigo-500 text-white hover:bg-indigo-600"
                    : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
                  "mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium"
                )}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-indigo-700">
        <div className="mx-auto max-w-7xl py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            Frequently asked questions
          </h2>
          <div className="mt-6 border-t border-indigo-300 border-opacity-25 pt-10">
            <dl className="space-y-10 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-12 md:space-y-0">
              {faqs.map((faq) => (
                <div key={faq.id}>
                  <dt className="text-lg font-medium leading-6 text-white">
                    {faq.question}
                  </dt>
                  <dd className="mt-2 text-base text-indigo-200">
                    {faq.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      <div className="bg-blue-50">
        <div className="mx-auto max-w-7xl py-12 px-4 sm:px-6 lg:flex lg:items-center lg:justify-between lg:py-24 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-blue-900 sm:text-4xl">
            <span className="block">Ready to dive in?</span>
            <span className="block text-blue-600">
              Start your free trial today.
            </span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <a
                href="https://app.shopdeploy.com/signup"
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 py-3 px-5 text-base font-medium text-white hover:bg-blue-700"
              >
                Get started
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
